import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../admin/components/shared/Tooltip.tsx';
import { CheckboxInput } from '../form/inputs/CheckboxInput.tsx';
import { Select } from '../common/Select';
import { buildSourceFileOptions } from './functional.js';
import {
  draftRowHasAnyMergedError,
  mergedFieldReasons,
  PROCESS_DRAFT_SOX_CYCLE_MAX_LEN,
  type ProcessDraftTableField,
  type ProcessDraftValidationRow,
} from './processDraftValidation.ts';
import { draftRowCellTdClass, DraftFieldCell } from '../../shared/draftReviewTableLayout.tsx';

export type ProcessDraftReviewRow = ProcessDraftValidationRow;

export type ProcessDraftReviewTableProps = {
  rows: ProcessDraftReviewRow[];
  selectedIds: Set<string>;
  /** When omitted, no “select all” header control is shown (per-row checkboxes only). */
  onToggleAll?: (checked: boolean) => void;
  onToggleRow: (processId: string, checked: boolean) => void;
  /** Required when `onToggleAll` is provided. */
  selectAllAriaLabel?: string;
  rowSelectAriaLabel: (row: ProcessDraftReviewRow) => string;
  getFieldValue: (row: ProcessDraftReviewRow, field: ProcessDraftTableField) => unknown;
  isFieldEditing: (processId: string, field: string) => boolean;
  startEditing: (processId: string, field: string) => void;
  stopEditing: () => void;
  updateField: (processId: string, field: string, value: string) => void;
  /** When true, show API `validation_errors` per column (bulk upload). */
  showBackendValidationErrors: boolean;
  /** When true, apply the same Yup rules as manual process create. Default true. */
  applyFrontendValidation?: boolean;
  /** Initial page size. Default 10. */
  initialPageSize?: number;
  /** Page size options in the footer selector. Default [5, 10, 25, 50]. */
  pageSizeOptions?: number[];
  /**
   * Route / context SOX cycle (e.g. `LEA`). When bulk-upload backend returns SOX mismatch errors,
   * the table only shows other column errors after this value matches the row’s SOX cell.
   */
  expectedSoxCycle?: string | null;
  /** Bulk upload shows SOX cycle; AI-assisted extraction hides it. Default true. */
  showSoxCycleColumn?: boolean;
  /** AI-assisted extraction can show all selected files and highlight row source files. */
  showSourceDocumentsColumn?: boolean;
  onUpdateSourceFileIds?: (processId: string, fileIds: string[]) => void;
};

type SourceDocument = {
  file_id?: unknown;
  file_name?: unknown;
  tags?: string[];
};

const inputBase =
  'box-border w-full rounded-md px-2 py-1.5 text-sm outline-none transition-colors';
const inputOk = 'border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20';
const inputErr = 'border border-red-500 bg-red-50/40 focus:border-red-600 focus:ring-2 focus:ring-red-500/20';

/** Fixed column shells — label and edit modes share the same outer width. */
const COL_SOX = 'w-[10rem] shrink-0';
const COL_MD = 'w-[10rem] shrink-0';
const COL_OWNER = 'w-[8rem] shrink-0';
const COL_SOURCE_DOCS = 'w-[15rem] shrink-0';
/** Description fills remaining table width; inner controls stay full width of the cell. */
const COL_DESC_FLEX = 'w-full min-w-[10rem]';

const SINGLE_CONTROL = 'min-h-[2.125rem]';
const DESC_CONTROL = 'min-h-[4.5rem]';
const TWO_LINE_VALUE = 'line-clamp-2 min-w-0 break-words leading-5';

const viewWrapOk =
  'box-border w-full rounded-md border border-transparent px-2 py-1.5';
const viewWrapErr =
  'box-border w-full rounded-md border border-red-500 bg-red-50/30 px-2 py-1.5 shadow-sm shadow-red-900/5';

const DEFAULT_PAGE_SIZES = [5, 10, 25, 50];
const SOURCE_FILE_SELECT_STYLES = {
  control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? '#60a5fa' : '#cbd5e1',
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused
      ? '0 0 0 3px rgba(59, 130, 246, 0.12)'
      : '0 1px 2px rgba(15, 23, 42, 0.05)',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: state.isFocused ? '#60a5fa' : '#93c5fd',
    },
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 36,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
    padding: '2px 10px',
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#94a3b8',
    fontWeight: 500,
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 36,
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    color: '#64748b',
    padding: 6,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 90,
    width: 224,
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    maxHeight: 180,
    padding: 6,
  }),
  option: (base: Record<string, unknown>, state: { isFocused?: boolean; isSelected?: boolean }) => ({
    ...base,
    borderRadius: 8,
    color: '#0f172a',
    backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8fafc' : '#ffffff',
    border: state.isSelected ? '1px solid #bfdbfe' : '1px solid transparent',
    padding: '7px 9px',
    ':active': {
      backgroundColor: '#dbeafe',
    },
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 90,
  }),
};
const SOURCE_FILE_MULTI_CHECKBOX_CLASSES = {
  optionContent: 'flex min-w-0 items-center gap-2 leading-5',
  checkbox: 'h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-blue-600 accent-blue-600',
  optionLabel: 'min-w-0 flex-1 truncate',
  summary: 'flex min-w-0 flex-1 items-center gap-1 overflow-hidden font-medium text-slate-900',
  summaryName: 'min-w-0 flex-1 truncate',
  summaryCount: 'shrink-0 whitespace-nowrap font-semibold text-blue-600',
};

/** Display / edit SOX cycle: uppercase, capped at PROCESS_DRAFT_SOX_CYCLE_MAX_LEN. */
export function formatSoxCycleDisplay(raw: unknown): string {
  const s = String(raw ?? '').trim().toUpperCase();
  return s ? s.slice(0, PROCESS_DRAFT_SOX_CYCLE_MAX_LEN) : '';
}

/** Show only the first unresolved message; the next appears after the user fixes the current one. */
function firstValidationMessage(reasons: string[]): string | null {
  return reasons.length > 0 ? reasons[0] : null;
}

/** Radix tooltip around a single trigger element (`asChild`). */
function CellTooltip({
  content,
  children,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
}): React.ReactElement {
  return <Tooltip content={content}>{children}</Tooltip>;
}

/** Enter edit on mousedown so blur from the previous input does not cancel the new cell. */
function beginDraftCellEdit(
  event: React.MouseEvent,
  processId: string,
  field: ProcessDraftTableField,
  startEditing: (processId: string, field: string) => void
): void {
  event.preventDefault();
  startEditing(processId, field);
}

function handleDraftCellKeyDown(
  event: React.KeyboardEvent,
  processId: string,
  field: ProcessDraftTableField,
  startEditing: (processId: string, field: string) => void
): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    startEditing(processId, field);
  }
}

type DraftEditableViewTriggerProps = {
  processId: string;
  field: ProcessDraftTableField;
  startEditing: (processId: string, field: string) => void;
  tooltip: string;
  className: string;
  children: React.ReactNode;
};

function DraftEditableViewTrigger({
  processId,
  field,
  startEditing,
  tooltip,
  className,
  children,
}: DraftEditableViewTriggerProps): React.ReactElement {
  return (
    <CellTooltip content={tooltip}>
      <div
        role="button"
        tabIndex={0}
        onMouseDown={(e) => beginDraftCellEdit(e, processId, field, startEditing)}
        onKeyDown={(e) => handleDraftCellKeyDown(e, processId, field, startEditing)}
        className={className}
      >
        {children}
      </div>
    </CellTooltip>
  );
}

export function ProcessDraftReviewTable({
  rows,
  selectedIds,
  onToggleAll,
  onToggleRow,
  selectAllAriaLabel,
  rowSelectAriaLabel,
  getFieldValue,
  isFieldEditing,
  startEditing,
  stopEditing,
  updateField,
  showBackendValidationErrors,
  applyFrontendValidation = true,
  initialPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  expectedSoxCycle = null,
  showSoxCycleColumn = true,
  showSourceDocumentsColumn = false,
  onUpdateSourceFileIds,
}: ProcessDraftReviewTableProps): React.ReactElement {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(() =>
    pageSizeOptions.includes(initialPageSize) ? initialPageSize : pageSizeOptions[0] ?? 10
  );

  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const showSelectAllHeader = typeof onToggleAll === 'function';

  useEffect(() => {
    setPageIndex(0);
  }, [rows.length]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
  const safePage = Math.min(pageIndex, totalPages - 1);
  const sliceStart = safePage * pageSize;

  useEffect(() => {
    if (pageIndex !== safePage) setPageIndex(safePage);
  }, [pageIndex, safePage]);

  const pageRows = useMemo(
    () => rows.slice(sliceStart, sliceStart + pageSize),
    [rows, sliceStart, pageSize]
  );

  const validationOpts = useMemo(
    () => ({
      showBackendErrors: showBackendValidationErrors,
      applyFrontendValidation,
      expectedSoxCycle: showSoxCycleColumn ? expectedSoxCycle : null,
      includeSoxCycle: showSoxCycleColumn,
    }),
    [showBackendValidationErrors, applyFrontendValidation, expectedSoxCycle, showSoxCycleColumn]
  );

  const selectableRows = useMemo(
    () =>
      rows.filter((row) => !draftRowHasAnyMergedError(row, getFieldValue, validationOpts)),
    [rows, getFieldValue, validationOpts]
  );

  /** Per current `rows` when header “select all” is shown (valid rows only). */
  const allSelected =
    showSelectAllHeader &&
    selectableRows.length > 0 &&
    selectableRows.every((r) => selectedIds.has(r.id));
  const someSelected =
    showSelectAllHeader &&
    selectableRows.length > 0 &&
    selectableRows.some((r) => selectedIds.has(r.id)) &&
    !allSelected;
  const selectAllDisabled = selectableRows.length === 0;

  useEffect(() => {
    if (!showSelectAllHeader) return;
    const el = headerCheckboxRef.current;
    if (el) el.indeterminate = someSelected;
  }, [someSelected, allSelected, rows.length, showSelectAllHeader]);

  const fromRow = rows.length === 0 ? 0 : sliceStart + 1;
  const toRow = Math.min(sliceStart + pageSize, rows.length);

  return (
    <TooltipProvider>
      <div className="bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-3 py-2.5 align-middle text-xs font-medium uppercase tracking-wider text-gray-500 ${
                    showSelectAllHeader ? 'text-center' : 'w-10'
                  }`}
                >
                  {showSelectAllHeader ? (
                    <CellTooltip content={selectAllAriaLabel ?? ''}>
                      <span className="inline-flex">
                        <CheckboxInput
                          ref={headerCheckboxRef}
                          checked={allSelected}
                          disabled={selectAllDisabled}
                          onChange={(e) => {
                            if (selectAllDisabled) return;
                            onToggleAll?.(e.target.checked);
                          }}
                          aria-label={selectAllAriaLabel}
                        />
                      </span>
                    </CellTooltip>
                  ) : (
                    <span className="sr-only">Select row</span>
                  )}
                </th>
                {showSoxCycleColumn ? (
                  <th
                    className={`${COL_SOX} px-3 py-2.5 text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500`}
                  >
                    SOX Cycle
                  </th>
                ) : null}
                <th
                  className={`${COL_MD} px-3 py-2.5 text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500`}
                >
                  Process Name
                </th>
                <th className="px-3 py-2.5 text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500">
                  Description
                </th>
                <th
                  className={`${COL_OWNER} px-3 py-2.5 text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500`}
                >
                  Owner
                </th>
                {showSourceDocumentsColumn ? (
                  <th
                    className={`${COL_SOURCE_DOCS} px-3 py-2.5 text-left align-middle text-xs font-medium uppercase tracking-wider text-gray-500`}
                  >
                    Source Files
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pageRows.map((process) => {
                const soxReasons = mergedFieldReasons(process, 'soxCycle', getFieldValue, validationOpts);
                const nameReasons = mergedFieldReasons(process, 'name', getFieldValue, validationOpts);
                const descReasons = mergedFieldReasons(process, 'description', getFieldValue, validationOpts);
                const ownerReasons = mergedFieldReasons(process, 'owner', getFieldValue, validationOpts);
                const soxHint = firstValidationMessage(soxReasons);
                const nameHint = firstValidationMessage(nameReasons);
                const descHint = firstValidationMessage(descReasons);
                const ownerHint = firstValidationMessage(ownerReasons);
                const soxInvalid = soxReasons.length > 0;
                const nameInvalid = nameReasons.length > 0;
                const descInvalid = descReasons.length > 0;
                const ownerInvalid = ownerReasons.length > 0;
                const rowHasAny = draftRowHasAnyMergedError(process, getFieldValue, validationOpts);
                const soxDisplay = formatSoxCycleDisplay(getFieldValue(process, 'soxCycle'));
                const nameStr = String(getFieldValue(process, 'name') ?? '');
                const descStr = String(getFieldValue(process, 'description') ?? '');
                const ownerStr = String(getFieldValue(process, 'owner') ?? '');
                const rowSelectLabel = rowSelectAriaLabel(process);
                const sourceDocuments: SourceDocument[] = Array.isArray(process.sourceDocuments)
                  ? process.sourceDocuments
                  : [];
                const selectedSourceFileIds = (Array.isArray(process.file_ids) ? process.file_ids : [])
                  .map((id: unknown) => String(id).trim())
                  .filter(Boolean);
                const sourceFileOptions = buildSourceFileOptions(sourceDocuments, selectedSourceFileIds);
                const canEditSourceFiles = typeof onUpdateSourceFileIds === 'function';
                const soxTip = soxDisplay || 'Click to edit SOX cycle';
                const nameTip = nameStr || 'Click to edit process name';
                const descTip = descStr || 'Click to edit description';
                const ownerTip = ownerStr || 'Click to add owner';
                const checkboxTip = nameStr ? `Select row: ${nameStr}` : rowSelectLabel;

                const isSelected = selectedIds.has(process.id);
                const rowBgClass =
                  isSelected && !rowHasAny
                    ? 'bg-blue-100/60 text-gray-900 hover:bg-blue-100/80'
                    : 'bg-gray-100/90 text-gray-600';
                const rowTdClass = draftRowCellTdClass(rowHasAny);

                return (
                  <tr key={process.id} className={rowBgClass}>
                    <td className={rowTdClass}>
                      <div className="flex items-center justify-center">
                        <CellTooltip
                          content={
                            rowHasAny
                              ? 'Fix validation errors on this row before you can select it.'
                              : checkboxTip
                          }
                        >
                          <span className="inline-flex">
                            <CheckboxInput
                              checked={selectedIds.has(process.id)}
                              disabled={rowHasAny}
                              onChange={(e) => {
                                if (rowHasAny) return;
                                onToggleRow(process.id, e.target.checked);
                              }}
                              aria-label={rowSelectLabel}
                            />
                          </span>
                        </CellTooltip>
                      </div>
                    </td>
                    {showSoxCycleColumn ? (
                      <td className={rowTdClass}>
                        <DraftFieldCell shellClassName={COL_SOX} hint={soxHint}>
                        {isFieldEditing(process.id, 'soxCycle') ? (
                          <input
                            value={soxDisplay}
                            maxLength={PROCESS_DRAFT_SOX_CYCLE_MAX_LEN}
                            onChange={(e) =>
                              updateField(
                                process.id,
                                'soxCycle',
                                e.target.value.toUpperCase().slice(0, PROCESS_DRAFT_SOX_CYCLE_MAX_LEN)
                              )
                            }
                            onBlur={stopEditing}
                            autoFocus
                            className={`${inputBase} ${SINGLE_CONTROL} ${soxInvalid ? inputErr : inputOk}`}
                            aria-invalid={soxInvalid}
                          />
                        ) : (
                          <DraftEditableViewTrigger
                            processId={process.id}
                            field="soxCycle"
                            startEditing={startEditing}
                            tooltip={soxTip}
                            className={`${SINGLE_CONTROL} flex cursor-pointer items-center text-sm font-medium text-gray-900 ${
                              soxInvalid ? viewWrapErr : `${viewWrapOk} hover:bg-gray-100`
                            }`}
                          >
                            {soxDisplay ? (
                              soxDisplay
                            ) : (
                              <span className="font-normal text-gray-400">Click to edit</span>
                            )}
                          </DraftEditableViewTrigger>
                        )}
                        </DraftFieldCell>
                      </td>
                    ) : null}
                    <td className={rowTdClass}>
                      <DraftFieldCell shellClassName={COL_MD} hint={nameHint}>
                        {isFieldEditing(process.id, 'name') ? (
                          <input
                            value={nameStr}
                            onChange={(e) => updateField(process.id, 'name', e.target.value)}
                            onBlur={stopEditing}
                            autoFocus
                            className={`${inputBase} ${SINGLE_CONTROL} ${nameInvalid ? inputErr : inputOk}`}
                            aria-invalid={nameInvalid}
                          />
                        ) : (
                          <DraftEditableViewTrigger
                            processId={process.id}
                            field="name"
                            startEditing={startEditing}
                            tooltip={nameTip}
                            className={`${SINGLE_CONTROL} flex cursor-pointer items-center text-sm font-medium text-gray-900 ${
                              nameInvalid ? viewWrapErr : `${viewWrapOk} hover:bg-gray-100`
                            }`}
                          >
                            {nameStr ? (
                              <span className={`${TWO_LINE_VALUE} w-full`}>{nameStr}</span>
                            ) : (
                              <span className="font-normal text-gray-400">Click to edit</span>
                            )}
                          </DraftEditableViewTrigger>
                        )}
                      </DraftFieldCell>
                    </td>
                    <td className={rowTdClass}>
                      <DraftFieldCell shellClassName={COL_DESC_FLEX} hint={descHint}>
                        {isFieldEditing(process.id, 'description') ? (
                          <textarea
                            rows={2}
                            value={descStr}
                            onChange={(e) => updateField(process.id, 'description', e.target.value)}
                            onBlur={stopEditing}
                            autoFocus
                            className={`${inputBase} ${DESC_CONTROL} resize-none ${descInvalid ? inputErr : inputOk}`}
                            aria-invalid={descInvalid}
                          />
                        ) : (
                          <DraftEditableViewTrigger
                            processId={process.id}
                            field="description"
                            startEditing={startEditing}
                            tooltip={descTip}
                            className={`${
                              rowHasAny ? DESC_CONTROL : `${SINGLE_CONTROL} flex items-center`
                            } cursor-pointer text-sm text-gray-700 ${
                              descInvalid ? viewWrapErr : `${viewWrapOk} hover:bg-gray-100`
                            }`}
                          >
                            {descStr ? (
                              <span className={`${TWO_LINE_VALUE} w-full`}>{descStr}</span>
                            ) : (
                              <span className="text-gray-400">Click to edit</span>
                            )}
                          </DraftEditableViewTrigger>
                        )}
                      </DraftFieldCell>
                    </td>
                    <td className={rowTdClass}>
                      <DraftFieldCell shellClassName={COL_OWNER} hint={ownerHint}>
                        {isFieldEditing(process.id, 'owner') ? (
                          <input
                            value={ownerStr}
                            onChange={(e) => updateField(process.id, 'owner', e.target.value)}
                            onBlur={stopEditing}
                            autoFocus
                            className={`${inputBase} ${SINGLE_CONTROL} ${ownerInvalid ? inputErr : inputOk}`}
                            aria-invalid={ownerInvalid}
                          />
                        ) : (
                          <DraftEditableViewTrigger
                            processId={process.id}
                            field="owner"
                            startEditing={startEditing}
                            tooltip={ownerTip}
                            className={`${SINGLE_CONTROL} flex cursor-pointer items-center text-sm text-gray-700 ${
                              ownerInvalid ? viewWrapErr : `${viewWrapOk} hover:bg-gray-100`
                            }`}
                          >
                            {ownerStr ? (
                              <span className={`${TWO_LINE_VALUE} w-full`}>{ownerStr}</span>
                            ) : (
                              <span className="text-gray-400">Click to add owner</span>
                            )}
                          </DraftEditableViewTrigger>
                        )}
                      </DraftFieldCell>
                    </td>
                    {showSourceDocumentsColumn ? (
                      <td className={rowTdClass}>
                        <div className={COL_SOURCE_DOCS}>
                          {sourceFileOptions.length > 0 ? (
                            <Select
                              key={`source-files-${process.id}`}
                              value={selectedSourceFileIds}
                              onChange={(value) => {
                                const fileIds = Array.isArray(value) ? value : value ? [value] : [];
                                onUpdateSourceFileIds?.(process.id, fileIds);
                              }}
                              options={sourceFileOptions}
                              isMulti
                              isMultiCheckbox
                              multiCheckboxSummaryTooltip
                              multiCheckboxClassNames={SOURCE_FILE_MULTI_CHECKBOX_CLASSES}
                              isSearchable
                              disabled={!canEditSourceFiles}
                              isClearable={false}
                              placeholder="Select source files"
                              placeholderOnFocus="Search source files"
                              placeholderOnBlur="Select source files"
                              noOptionsMessage={() => 'No source files available'}
                              styles={SOURCE_FILE_SELECT_STYLES}
                              className="w-[16.5rem] max-w-full text-xs"
                              menuPlacement="auto"
                              aria-label={`Select source files for ${nameStr || 'process'}`}
                            />
                          ) : (
                            <span className="text-xs text-gray-400">No source files</span>
                          )}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-gray-600">
          Showing{' '}
          <span className="font-medium text-gray-800">
            {fromRow}–{toRow}
          </span>{' '}
          of <span className="font-medium text-gray-800">{rows.length}</span>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <span className="whitespace-nowrap">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 shadow-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={safePage <= 0}
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[5.5rem] text-center text-xs text-gray-600">
              Page {safePage + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      </div>
    </TooltipProvider>
  );
}
